import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import {
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  Typography,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Page, Text, View, Document, PDFViewer } from '@react-pdf/renderer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon from '@mui/icons-material/Percent';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useMaterialUIController } from "context";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpDataGridSx, StatusPill } from "components/financepro";
import { useApi, usePerfilSucursal } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa } from "lib/multiempresa";
import { estadoPrestamo, montoANumero } from "lib/prestamos";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";

// Pill de estado del préstamo distinguiendo saldado de cancelado manual.
function pillEstadoPrestamo(status, balancePendiente) {
  const e = estadoPrestamo(status, balancePendiente);
  if (e === 'activo') return <StatusPill label="Activo" variant="success" />;
  if (e === 'saldado') return <StatusPill label="Saldado" variant="info" />;
  if (e === 'cancelado') return <StatusPill label="Cancelado" variant="neutral" />;
  if (e === 'incobrable') return <StatusPill label="Incobrable" variant="danger" />;
  return <StatusPill label={status || '—'} variant="info" />;
}

const balancePendienteFila = (r) => montoANumero(r?.balcap_pend) + montoANumero(r?.balint_pend);

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: FP.shadow.md, transform: 'translateY(-2px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ p: 2.4, '&:last-child': { pb: 2.4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: '1.4rem',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}14`,
              color: accent,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

/**
 * @param embedded  Renderiza sin PageHeader ni fondo propio para usarse como
 *                  listado dentro de otra pantalla (p.ej. Préstamos). En este
 *                  modo carga automáticamente el mes en curso.
 * @param refreshToken  Al cambiar su valor se recarga el listado.
 */
function InformePrestamo({ embedded = false, refreshToken = 0 } = {}) {
  const hoyISO = () => new Date().toISOString().slice(0, 10);
  const inicioMesISO = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  };
  const [presentarpdf, setPresentarpdf] = useState(false)
  const [fecha1, setFecha1] = useState(embedded ? inicioMesISO() : "")
  const [fecha2, setFecha2] = useState(embedded ? hoyISO() : "")
  const [nosucursal, setNosucursal] = useState(null)
  const [sucursal, setSucursal] = useState([])
  const [rows, setRows] = useState([])
  const [filtroStatus, setFiltroStatus] = useState('todos')
  // General = financiamientos de motores; Personal = préstamos personales
  // (prestamo.ppersonal = 'S', mismo corte que balance y saldados).
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [controller] = useMaterialUIController()
  const { nombreNegocio } = controller
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const navigate = useNavigate();
  // Con el programa "expediente", un clic en la fila abre el expediente del préstamo.
  const puedeExpediente = tienePermisosModulo(controller.roleDetalles, "expediente");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    post('/api/recuperasucursales', { fechadesde: fecha1, fechahasta: fecha2, sucursal: nosucursal })
      .then(function ({ status, data }) {
        if (status === 200) {
          const list = data["sucursales"] || []
          // Un usuario que no es admin queda fijado a su sucursal.
          if (!esadmin && sucursalPropia) {
            const propia = list.filter(
              (s) => String(s.label || "").split("-")[0].trim() === sucursalPropia
            )
            setSucursal(propia.length ? propia : list)
            setNosucursal(propia[0] || null)
          } else {
            setSucursal(list)
          }
        }
        if (status === 400) {
          setRows([])
          showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 })
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'DOC #', width: 80, minWidth: 70, align: 'center', headerAlign: 'center' },
      { field: 'fecha', headerName: 'Fecha', type: 'string', minWidth: 100, flex: 0.6, editable: true },
      // Multi-empresa: empresa del préstamo (solo con el modo activo)
      ...(multiempresa.activo
        ? [
            {
              field: 'empresa',
              headerName: 'Empresa',
              minWidth: 120,
              flex: 0.6,
              valueGetter: (value, row) => nombreEmpresa(multiempresa.empresas, row.empresa || 1),
            },
          ]
        : []),
      { field: 'nombres', headerName: 'Nombre', type: 'string', minWidth: 130, flex: 1 },
      { field: 'cedula', headerName: 'Cédula', type: 'string', flex: 1, minWidth: 120, editable: true },
      { field: 'chasis', headerName: 'Chasis', type: 'string', flex: 1, minWidth: 140 },
      { field: 'cantsol', headerName: 'Cantidad', type: 'string', minWidth: 110, flex: 0.7 },
      { field: 'plazo', headerName: 'Plazo', type: 'string', minWidth: 90, flex: 0.5 },
      { field: 'interes', headerName: 'Interés', type: 'string', minWidth: 90, flex: 0.5 },
      { field: 'balcap_pend', headerName: 'Cap. pend.', type: 'string', minWidth: 110, flex: 0.55 },
      { field: 'balint_pend', headerName: 'Int. pend.', type: 'string', minWidth: 110, flex: 0.55 },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        minWidth: 110,
        flex: 0.5,
        renderCell: (params) => pillEstadoPrestamo(params.value, balancePendienteFila(params.row)),
      },
      {
        field: 'g',
        headerName: '',
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <ContentCopyIcon
            sx={{ cursor: 'pointer', color: FP.color.textSecondary, '&:hover': { color: FP.color.navy } }}
            onClick={() =>
              copyTextToClipboard(
                'https:motoprestamo.suitorbit.com/expediente/' +
                localStorage.getItem('id') + '/' + params.row.cedula
              )
            }
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiempresa]
  );

  const filasVista = useMemo(() => {
    // Búsqueda libre por nombre/apellidos, chasis o cédula (sobre lo cargado).
    const q = busqueda.trim().toLowerCase();
    const porTexto = !q
      ? rows
      : rows.filter((r) => {
          const nombre = `${r.nombres ?? ''} ${r.apellidos ?? ''}`.toLowerCase();
          const chasis = String(r.chasis ?? '').toLowerCase();
          const cedula = String(r.cedula ?? '').replace(/\D/g, '');
          return (
            nombre.includes(q) ||
            chasis.includes(q) ||
            (cedula && cedula.includes(q.replace(/\D/g, '') || '\u0000'))
          );
        });
    // Tipo de préstamo (sobre lo cargado, como el resto de filtros de vista).
    const esPersonal = (r) => String(r.ppersonal ?? '').trim() !== '';
    const porTipo =
      filtroTipo === 'todos'
        ? porTexto
        : porTexto.filter((r) => esPersonal(r) === (filtroTipo === 'personal'));
    if (filtroStatus === 'todos') return porTipo;
    // Saldado: balance pendiente (capital + interés) <= 1, el mismo criterio
    // del reporte de préstamos saldados del backend (sin mirar el status:
    // al pagarse completo el préstamo queda con status 'C', igual que un
    // cancelado manual). "Cancelados" queda para los cancelados con deuda.
    const esSaldado = (r) => balancePendienteFila(r) <= 1;
    return porTipo.filter((r) => {
      const s = String(r.status ?? '').trim().toUpperCase();
      if (filtroStatus === 'activos') return s === 'A' && !esSaldado(r);
      if (filtroStatus === 'saldados') return esSaldado(r);
      if (filtroStatus === 'cancelados') return s === 'C' && !esSaldado(r);
      // Incobrable (deuda castigada desde el expediente): va aparte de los
      // cancelados aunque tampoco sea cartera activa.
      if (filtroStatus === 'incobrables') return s === 'I';
      return true;
    });
  }, [rows, filtroStatus, filtroTipo, busqueda]);

  function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  // En modo embebido el listado carga solo (al montar y cuando la pantalla
  // contenedora incrementa refreshToken, p.ej. tras cancelar un préstamo).
  useEffect(() => {
    if (embedded && fecha1 && fecha2) procesarrequerimiento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, refreshToken]);

  function procesarrequerimiento(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    post('/api/informesprestamos', {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: nosucursal,
      ...(multiempresa.activo ? { empresa: Number(empresaFiltro) } : {}),
    })
      .then(function ({ status, data }) {
        if (status === 200) {
          setRows(data["prestamo"])
        }
        if (status === 400) {
          setRows([])
          showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 })
        }
      })
  }

  // PDF con anchos proporcionales (las columnas de 100px fijos se salían de
  // la página A4) y las mismas columnas del grid; sin `fixed` en las celdas
  // de datos (en react-pdf repite el elemento en cada página).
  const MyDocument = () => {
    const parseN = (v) => parseFloat(String(v ?? '0').split(',').join('')) || 0;
    const fmtN = (v) => parseN(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const tvalor = filasVista.reduce((acc, filas) => acc + parseN(filas.cantsol), 0);
    const tcap = filasVista.reduce((acc, filas) => acc + parseN(filas.balcap_pend), 0);
    const tint = filasVista.reduce((acc, filas) => acc + parseN(filas.balint_pend), 0);
    const W = { prest: "7%", fecha: "10%", cliente: "24%", cedula: "13%", cant: "12%", plazo: "6%", interes: "7%", cap: "12%", int: "12%", status: "6%" };
    const celda = { padding: 2 };
    const num = { ...celda, textAlign: "right" };
    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document>
          <Page size="A4" style={{ backgroundColor: "#FFFFFF", paddingTop: 15, paddingBottom: 25, paddingHorizontal: 18 }}>
            <View style={{ textAlign: "center", fontSize: 14, textTransform: "uppercase" }} fixed>
              <Text>{nombreNegocio}</Text>
            </View>
            <View style={{ textAlign: "center", fontSize: 9, textTransform: "uppercase", marginBottom: 8 }} fixed>
              <Text>Reporte de Préstamos</Text>
            </View>
            <View style={{ flexDirection: "row", fontSize: 7.5, backgroundColor: "#E6EAF1", color: "black", fontWeight: "bold" }} fixed>
              <Text style={{ ...celda, width: W.prest }}>Prest#</Text>
              <Text style={{ ...celda, width: W.fecha }}>Fecha</Text>
              <Text style={{ ...celda, width: W.cliente }}>Cliente</Text>
              <Text style={{ ...celda, width: W.cedula }}>Cédula</Text>
              <Text style={{ ...num, width: W.cant }}>Cantidad</Text>
              <Text style={{ ...num, width: W.plazo }}>Plazo</Text>
              <Text style={{ ...num, width: W.interes }}>Int.%</Text>
              <Text style={{ ...num, width: W.cap }}>Cap. pend.</Text>
              <Text style={{ ...num, width: W.int }}>Int. pend.</Text>
              <Text style={{ ...celda, width: W.status, textAlign: "center" }}>St.</Text>
            </View>
            {filasVista.map((filas, i) =>
              <View key={i} wrap={false} style={{ flexDirection: "row", fontSize: 7.5, color: "black", borderBottom: "0.5px solid #DDD" }}>
                <Text style={{ ...celda, width: W.prest }}>{filas.id}</Text>
                <Text style={{ ...celda, width: W.fecha }}>{filas.fecha}</Text>
                <Text style={{ ...celda, width: W.cliente }}>{`${filas.nombres || ""} ${filas.apellidos || ""}`.trim()}</Text>
                <Text style={{ ...celda, width: W.cedula }}>{filas.cedula}</Text>
                <Text style={{ ...num, width: W.cant }}>{fmtN(filas.cantsol)}</Text>
                <Text style={{ ...num, width: W.plazo }}>{filas.plazo}</Text>
                <Text style={{ ...num, width: W.interes }}>{filas.interes}</Text>
                <Text style={{ ...num, width: W.cap }}>{fmtN(filas.balcap_pend)}</Text>
                <Text style={{ ...num, width: W.int }}>{fmtN(filas.balint_pend)}</Text>
                <Text style={{ ...celda, width: W.status, textAlign: "center" }}>{filas.status}</Text>
              </View>
            )}
            <View style={{ flexDirection: "row", fontSize: 7.5, color: "black", marginTop: 6, fontWeight: "bold" }}>
              <Text style={{ ...celda, width: W.prest }}>Totales</Text>
              <Text style={{ ...celda, width: W.fecha }}> </Text>
              <Text style={{ ...celda, width: W.cliente }}>{`${filasVista.length} préstamos`}</Text>
              <Text style={{ ...celda, width: W.cedula }}> </Text>
              <Text style={{ ...num, width: W.cant }}>{fmtN(tvalor)}</Text>
              <Text style={{ ...num, width: W.plazo }}> </Text>
              <Text style={{ ...num, width: W.interes }}> </Text>
              <Text style={{ ...num, width: W.cap }}>{fmtN(tcap)}</Text>
              <Text style={{ ...num, width: W.int }}>{fmtN(tint)}</Text>
              <Text style={{ ...celda, width: W.status }}> </Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (filasVista.length == 0) {
      showAlert({ message: "No hay datos para procesar este reporte", color: "error", autoHideDuration: 3000 })
    } else {
      setPresentarpdf(true)
    }
  }

  const totalCantidad = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.cantsol || "0").split(',').join('')) || 0), 0);
  const totalBalCapPend = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.balcap_pend ?? "0").split(',').join('')) || 0), 0);
  const totalBalIntPend = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.balint_pend ?? "0").split(',').join('')) || 0), 0);

  const exportBtn = (
    <Button
      variant="contained"
      startIcon={<FileDownloadRoundedIcon />}
      disabled={filasVista.length === 0}
      onClick={handlepdf}
      sx={{
        bgcolor: FP.color.navy,
        color: '#FFFFFF',
        borderRadius: 1.4,
        px: 2.2,
        height: 38,
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: 700,
        '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
        '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
      }}
    >
      Export Data
    </Button>
  );

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : {
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      {!embedded && (
        <PageHeader
          title="Préstamos"
          subtitle="Informe de Préstamos"
          cta={exportBtn}
        />
      )}

      <Box sx={{ p: embedded ? 0 : { xs: 2, sm: 2.5, md: 3 } }}>
        {embedded && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{exportBtn}</Box>
        )}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  id="fecha-desde"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  id="fecha-hasta"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  disabled={!esadmin}
                  id="sucursal-autocomplete"
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.label || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionLabel = typeof option === 'string' ? option : option?.label;
                    const valueLabel = typeof value === 'string' ? value : value?.label;
                    return optionLabel === valueLabel;
                  }}
                  onChange={(event, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              {multiempresa.activo && (
                <Grid item xs={12} sm={6} md={3}>
                  <SelectorEmpresa
                    value={empresaFiltro}
                    onChange={setEmpresaFiltro}
                    empresas={multiempresa.empresas}
                    sx={INPUT_SX}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" sx={INPUT_SX}>
                  <InputLabel id="filtro-status-label" shrink>Estado del préstamo</InputLabel>
                  <Select
                    labelId="filtro-status-label"
                    id="filtro-status"
                    label="Estado del préstamo"
                    notched
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value)}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="saldados">Saldados</MenuItem>
                    <MenuItem value="cancelados">Cancelados</MenuItem>
                    <MenuItem value="incobrables">Incobrables</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" sx={INPUT_SX}>
                  <InputLabel id="filtro-tipo-label" shrink>Tipo de préstamo</InputLabel>
                  <Select
                    labelId="filtro-tipo-label"
                    id="filtro-tipo"
                    label="Tipo de préstamo"
                    notched
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="personal">Personal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            <StatCard
              label="Registros (vista)"
              value={String(filasVista.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total cantidad"
              value={`$${totalCantidad.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
            <StatCard
              label="Capital pendiente"
              value={`$${totalBalCapPend.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AccountBalanceIcon}
              accent={FP.color.successDark}
            />
            <StatCard
              label="Interés pendiente"
              value={`$${totalBalIntPend.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={PercentIcon}
              accent={FP.color.warning}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Listado de Préstamos
            </Typography>
            {rows.length > 0 && (
              <TextField
                size="small"
                placeholder="Buscar por nombre, chasis o cédula"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                sx={{
                  minWidth: { xs: '100%', sm: 280 },
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    borderRadius: 1.2,
                    bgcolor: '#FFFFFF',
                    fontSize: '0.86rem',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {filasVista.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              filasVista.length > 0 ? (
                <>
                  <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                    <DataGrid
                      rows={filasVista}
                      columns={columns}
                      getRowId={(row) => (row.id != null ? String(row.id) : `${row.cedula}-${row.fecha}`)}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      checkboxSelection
                      disableRowSelectionOnClick
                      onRowClick={(params) => {
                        if (puedeExpediente && params.row.id != null) {
                          navigate(`/prestamos/expediente/${params.row.id}`);
                        }
                      }}
                      rowHeight={42}
                      sx={{
                        minWidth: { xs: 720, sm: '100%' },
                        height: 520,
                        ...fpDataGridSx,
                        ...(puedeExpediente ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : {}),
                      }}
                      autoHeight={false}
                    />
                  </Box>
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.4 }}>
                    {filasVista.map((row, idx) => {
                      const pill = pillEstadoPrestamo(row.status, balancePendienteFila(row));
                      return (
                        <Box
                          key={row.id != null ? String(row.id) : `${row.cedula}-${row.fecha}-${idx}`}
                          onClick={() => {
                            if (puedeExpediente && row.id != null) navigate(`/prestamos/expediente/${row.id}`);
                          }}
                          sx={{
                            border: `1px solid ${FP.color.border}`,
                            borderRadius: 2,
                            p: 1.5,
                            bgcolor: '#FFFFFF',
                            boxShadow: FP.shadow.soft,
                            cursor: puedeExpediente ? 'pointer' : 'default',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1rem', lineHeight: 1.2 }}>
                                Préstamo #{row.id}
                              </Typography>
                              <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.85rem', fontWeight: 600, mt: 0.25, wordBreak: 'break-word' }}>
                                {[row.nombres, row.apellidos].filter(Boolean).join(' ') || '—'}
                              </Typography>
                              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.1 }}>
                                {row.fecha} · Cédula {row.cedula}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              {pill}
                              <ContentCopyIcon
                                sx={{ cursor: 'pointer', color: FP.color.textSecondary, fontSize: 18, ml: 0.5, '&:hover': { color: FP.color.navy } }}
                                onClick={() =>
                                  copyTextToClipboard(
                                    'https:motoprestamo.suitorbit.com/expediente/' +
                                    localStorage.getItem('id') + '/' + row.cedula
                                  )
                                }
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1 }}>
                            {[
                              ['Cantidad', row.cantsol],
                              ['Plazo', row.plazo],
                              ['Interés', row.interes],
                              ['Cap. pend.', row.balcap_pend],
                              ['Int. pend.', row.balint_pend],
                            ].map(([label, value]) => (
                              <Box key={label}>
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                  {label}
                                </Typography>
                                <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.92rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                                  {value ?? '—'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    Ningún préstamo coincide con los filtros seleccionados
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                    Cambie el estado o tipo de préstamo, o procese de nuevo con otro rango.
                  </Typography>
                </Box>
              )
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth={true}
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: 'auto', p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
export default InformePrestamo
